import React from 'react';
import { SOFT, SOFT_HOVER, TEXT, FOCUS_RING, normaliseTone, cx } from './toneStyles';

/**
 * IconButton — icon-only button with a mandatory accessible name.
 *
 * Props
 *   icon     Md icon component (required)
 *   label    required → aria-label + title
 *   size     'sm' (36px) | 'md' (44px, default)
 *   variant  'ghost' (default) | 'soft' | 'danger'
 *   tone     colours the icon (ghost) or the tinted disc (soft); see toneStyles
 *
 * Replaces hand-rolled `p-2 rounded-lg` icon buttons and every &times; glyph.
 */
const SIZES = { sm: 'w-9 h-9 min-w-[36px] min-h-[36px]', md: 'w-11 h-11 min-w-[44px] min-h-[44px]' };
const ICON = { sm: 'w-5 h-5', md: 'w-5 h-5' };

export default function IconButton({
  icon: Icon,
  label,
  size = 'md',
  variant = 'ghost',
  tone,
  className = '',
  type = 'button',
  ...props
}) {
  const t = normaliseTone(tone);
  let look;
  if (variant === 'danger') {
    look = cx(
      'text-danger-600 dark:text-danger-400',
      'hover:bg-danger-50 active:bg-danger-100 dark:hover:bg-danger-900/20 dark:active:bg-danger-900/30'
    );
  } else if (variant === 'soft') {
    look = cx(SOFT[t], SOFT_HOVER[t]);
  } else {
    look = cx(
      tone ? TEXT[t] : 'text-ink-muted dark:text-night-muted',
      'hover:bg-surface-100 active:bg-surface-200 dark:hover:bg-night-card dark:active:bg-night-border'
    );
  }

  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex items-center justify-center rounded-full flex-shrink-0',
        SIZES[size] || SIZES.md,
        look,
        'active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
        'transition-[background-color,transform] duration-fast ease-out',
        FOCUS_RING,
        className
      )}
      {...props}
    >
      {Icon && <Icon className={ICON[size] || ICON.md} aria-hidden="true" />}
    </button>
  );
}
